import React, { useContext, useState } from "react";
import { setLHFilter } from "../../../helpers/session";

const LoadHistoryContext = React.createContext();

export const useLoadHistoryContext = () => useContext(LoadHistoryContext);

export const initialLoadFilterState = {
  duration: [],
  loadWeight: [],
  loadGrossWeight: [],
  loadLightWeight: [],
  loadTareWeight: [],
  status: [],
  fieldShape: [],
  crop: [],
  variety: [],
  vendor: [],
  offloadSite: [],
  storage: [],
  pipe: [],
  crew: [],
  targetEg: [],
  sourceEg: [],
};

export const LoadHistoryProvider = ({ children }) => {
  const [filter, setFilter] = useState(initialLoadFilterState);
  const [searchText, setSearchText] = useState("");
  const [filterText, setFilterText] = useState("");

  const [selectedCrew, setSelectedCrew] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedOffload, setSelectedOffload] = useState(null);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [selectedPipe, setSelectedPipe] = useState(null);
  const [loadWeight, setLoadWeight] = useState(null);
  const [loadGrossWeight, setLoadGrossWeight] = useState(null);
  const [loadLightWeight, setLoadLightWeight] = useState(null);
  const [loadTareWeight, setLoadTareWeight] = useState(null);

  const [bulkEditLoadList, setBulkEditLoadList] = useState([]);
  const [selectedLoadLength, setSelectedLoadLength] = useState(0);
  const [enableBulkEdit, setLoadHistoryBulkEditStatus] = useState(false);
  const [showDeletedLoads, setShowDeletedLoads] = useState(false);

  const parseLoadInfo = (item) => {
    const load = { load_activity_uuid: item.load_activity_uuid };

    if (selectedCrew) {
      load.crew_uuid = selectedCrew.crewId;
      load.crew_name = selectedCrew.crewName;
    }
    if (selectedField) {
      load.field_geo_region_uuid = selectedField.regionId;
      load.field_geo_region_name = selectedField.regionName;
      load.field_geo_shape_uuid = selectedField.value;
      load.field_geo_shape_name = selectedField.label;
    }
    if (selectedOffload) {
      load.offload_geo_region_uuid = selectedOffload.regionId;
      load.offload_geo_region_name = selectedOffload.regionName;
      load.offload_geo_shape_uuid = selectedOffload.value;
      load.offload_geo_shape_name = selectedOffload.label;

      const storage = selectedStorage || {};
      load.offload_geo_storage_location_uuid = storage.value || null;
      load.offload_geo_storage_location_name = storage.label || null;
    }
    if (selectedPipe) {
      load.pipe = `Pipe ${selectedPipe.value}`;
      load.pipeId = selectedPipe.value;
    }

    load.tare_weight = loadTareWeight
      ? Number(loadTareWeight)
      : item.tare_weight
      ? item.tare_weight
      : 0;

    load.light_weight = loadLightWeight
      ? Number(loadLightWeight)
      : item.light_weight
      ? item.light_weight
      : 0;

    load.load_gross_weight =
      loadGrossWeight > 0
        ? loadGrossWeight
        : item.load_gross_weight
        ? item.load_gross_weight
        : 0;

    load.load_capacity =
      Number(load.load_gross_weight) -
      (Number(load.light_weight) + Number(load.tare_weight));

    return load;
  };

  const handleBulkEditLoadSelect = (loadList, checked, override) => {
    const existingSelectedLoadIds = bulkEditLoadList.map(
      (l) => l.load_activity_uuid
    );
    if (checked) {
      const totalLoadList = override
        ? [...loadList]
        : [
            ...bulkEditLoadList,
            ...loadList.filter(
              (load) =>
                !existingSelectedLoadIds.includes(load.load_activity_uuid)
            ),
          ];

      const updatedLoadList = totalLoadList.map(parseLoadInfo).slice(0, 50);
      setSelectedLoadLength(totalLoadList.length);
      setBulkEditLoadList(updatedLoadList);
    } else {
      const updatedLoadList = bulkEditLoadList.filter(
        (load) =>
          !loadList
            .map((l) => l.load_activity_uuid)
            .includes(load.load_activity_uuid)
      );

      setSelectedLoadLength(updatedLoadList.length);
      setBulkEditLoadList(updatedLoadList);
    }
  };

  const handleFilterSelection = (name, id, label, checked) => {
    let selectedItems = [...filter[name]];
    if (checked) selectedItems.push({ id, label });
    else selectedItems = selectedItems.filter((item) => item.id !== id);
    const finalFilter = { ...filter, [name]: selectedItems };
    setLHFilter(finalFilter);
    setFilter(finalFilter);
  };

  const clearEditLoadOptions = () => {
    setSelectedCrew(null);
    setSelectedField(null);
    setSelectedOffload(null);
    setSelectedStorage(null);
    setSelectedPipe(null);
    setLoadWeight(null);
    setLoadGrossWeight(null);
    setLoadLightWeight(null);
    setLoadTareWeight(null);
    setSelectedLoadLength(0);
  };

  const resetBulkEdit = (clearFilter = true) => {
    setBulkEditLoadList([]);
    clearEditLoadOptions();
    if (clearFilter) setFilter(initialLoadFilterState);
  };

  const value = {
    filter,
    setFilter,
    handleFilterSelection,
    searchText,
    setSearchText,
    filterText,
    setFilterText,
    selectedCrew,
    setSelectedCrew,
    selectedField,
    setSelectedField,
    selectedOffload,
    setSelectedOffload,
    selectedStorage,
    setSelectedStorage,
    selectedPipe,
    setSelectedPipe,
    loadWeight,
    setLoadWeight,
    loadGrossWeight,
    setLoadGrossWeight,
    loadLightWeight,
    setLoadLightWeight,
    loadTareWeight,
    setLoadTareWeight,
    clearEditLoadOptions,
    bulkEditLoadList,
    selectedLoadLength,
    handleBulkEditLoadSelect,
    resetBulkEdit,
    enableBulkEdit,
    setLoadHistoryBulkEditStatus,
    showDeletedLoads,
    setShowDeletedLoads,
  };

  return (
    <LoadHistoryContext.Provider value={value}>
      {children}
    </LoadHistoryContext.Provider>
  );
};